"use client";

import {
  Token,
  ChainType,
  getTokens,
  getChains,
  getToken as getTokenSdk,
} from "@lifi/sdk";
import { isSolanaAddress } from "@/lib/solana";
import { isAddress as isEvmAddress } from "viem";

interface GetTokensForChainProps {
  chainId: number;
  offset?: number;
  search?: string;
}

const ETH_NATIVE_TOKEN: Token = {
  chainId: 1,
  address: "0x0000000000000000000000000000000000000000",
  symbol: "ETH",
  name: "Ethereum",
  decimals: 18,
  logoURI:
    "https://tokens.1inch.io/0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee.png",
  priceUSD: "0",
};

export const getTokensForChain = async ({
  chainId,
  offset = 0,
  search,
}: GetTokensForChainProps) => {
  const tokens = await getTokens({
    chains: [chainId],
    chainTypes: [ChainType.EVM, ChainType.SVM],
  });

  if (!tokens.tokens[chainId]) {
    throw new Error(`No tokens found for chainId: ${chainId}`);
  }

  const chainTokens = tokens.tokens[chainId];

  // Helper function to convert token format
  const formatToken = (token: any): Token => ({
    chainId: token.chainId,
    address: token.address,
    symbol: token.symbol,
    name: token.name,
    decimals: token.decimals,
    logoURI: token.logoURI || "",
    priceUSD: token.priceUSD,
  });

  // Handle address search
  if (search && (isEvmAddress(search) || isSolanaAddress(search))) {
    const targetToken = await getToken(chainId, search);
    return {
      tokens: targetToken ? [targetToken] : [],
      hasMore: false,
      total: 1,
    };
  }

  // Handle name/symbol search or return all
  const filteredTokens = search
    ? chainTokens.filter(
        (token) =>
          token.symbol.toLowerCase().includes(search.toLowerCase()) ||
          token.name.toLowerCase().includes(search.toLowerCase())
      )
    : chainTokens;

  const formattedTokens = filteredTokens
    .slice(offset, offset + 20)
    .map(formatToken);

  const shouldInjectEthNative =
    chainId === 1 &&
    (offset === 0 || formattedTokens.length === 0) &&
    !formattedTokens.some(
      (token) => token.symbol.toUpperCase() === "ETH" && token.address,
    );

  if (shouldInjectEthNative) {
    formattedTokens.unshift(ETH_NATIVE_TOKEN);
  }

  return {
    tokens: formattedTokens,
    hasMore: offset + 20 < filteredTokens.length,
    total: filteredTokens.length,
  };
};

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const getSupportedChains = async ({
  search,
  maxRetries = 3, // Maximum number of retry attempts
  initialDelay = 800, // Initial delay in milliseconds
}: {
  search?: string;
  maxRetries?: number;
  initialDelay?: number;
}) => {
  let lastError: Error | null = null;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      const chains = await getChains({
        chainTypes: [ChainType.EVM, ChainType.SVM],
      });

      if (!chains) throw new Error("No chains found");

      const filteredChains = chains.filter(
        (chain) =>
          chain.mainnet && // Only show mainnet chains
          (!search ||
            chain.name.toLowerCase().includes(search.toLowerCase()) ||
            chain.key.toLowerCase().includes(search.toLowerCase()))
      );

      const ethereumMainnet = chains.find((chain) => chain.id === 1);
      const includeEthereum =
        ethereumMainnet &&
        (!search ||
          ethereumMainnet.name.toLowerCase().includes(search.toLowerCase()) ||
          ethereumMainnet.key.toLowerCase().includes(search.toLowerCase()));

      const chainsWithEthereum = includeEthereum
        ? [
            ethereumMainnet,
            ...filteredChains.filter((chain) => chain.id !== 1),
          ]
        : filteredChains;

      return {
        chains: chainsWithEthereum.map((chain) => ({
          chainType: chain.chainType,
          name: chain.name,
          logoURI: chain.logoURI,
          id: chain.id,
          symbol: chain.key,
        })),
        total: filteredChains.length,
      };
    } catch (error) {
      lastError = error as Error;
      if (attempt < maxRetries - 1) {
        // Calculate delay with exponential backoff
        const backoffDelay = initialDelay * Math.pow(2, attempt);
        await delay(backoffDelay);
        console.warn(
          `Retry attempt ${attempt + 1} for getSupportedChains after ${backoffDelay}ms`
        );
      }
    }
  }

  // If we've exhausted all retries, throw the last error
  throw lastError || new Error("Failed to fetch chains after all retries");
};

export type SupportedChain = Awaited<
  ReturnType<typeof getSupportedChains>
>["chains"][number];

export const getToken = async (chainId: number, address: string) => {
  try {
    return await getTokenSdk(chainId, address);
  } catch (error) {
    // console.error(error);
    return null;
  }
};
